import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Traducciones completas
const translations = {
  es: {
    // Application questionnaire - nested keys
    app: {
      block1: { title: 'Datos Básicos', subtitle: 'Cuéntanos sobre ti' },
      block2: { title: 'Estilo de Vida', subtitle: 'Tu forma de vivir' },
      block3: { title: 'Intención', subtitle: '¿Qué estás buscando?' },
      block4: { title: 'Personalidad', subtitle: 'Cómo eres en citas' },
      block5: { title: 'Conexión', subtitle: 'Lo que te atrae' },
      block6: { title: 'Nivel Premium', subtitle: 'Tu compromiso' },
      block7: { title: 'Match Inteligente', subtitle: 'Tu match ideal' },
      block8: { title: 'Exclusividad', subtitle: '¿Por qué Soul Mate Dates?' },
      step: 'Paso',
      of: 'de',
      saving: 'Guardando...',
      saved: 'Guardado',
      select: 'Seleccionar',
      login_required: 'Inicia sesión para aplicar',
      login_message: 'Necesitas una cuenta para completar tu aplicación a Soul Mate Dates.',
      login_button: 'Continuar con Google',
      error: { submit: 'Error al enviar la aplicación' },
      fields: {
        name: 'Nombre completo',
        age: 'Edad',
        gender: 'Género',
        city: 'Ciudad',
        occupation: 'Ocupación',
        instagram: 'Instagram (opcional)',
        interested_in: '¿Te interesan?'
      },
      placeholders: {
        name: 'Tu nombre',
        age: 'Tu edad',
        city: '¿Dónde vives?',
        occupation: '¿A qué te dedicas?',
        personality: 'Cuéntanos cómo te ven los demás...',
        dealbreakers: 'Sé honesto/a sobre tus límites...',
        ideal: 'Describe a tu persona ideal...',
        incompatible: '¿Qué tipo de personas prefieres evitar?',
        why_join: 'Cuéntanos tu motivación para unirte a nuestra comunidad exclusiva...'
      },
      gender: { male: 'Hombre', female: 'Mujer', other: 'Otro' },
      interested: { men: 'Hombres', women: 'Mujeres', all: 'Ambos' },
      q: {
        lifestyle: '¿Cómo describirías tu estilo de vida?',
        freetime: '¿Qué valoras más en tu tiempo libre?',
        looking_for: '¿Qué estás buscando actualmente?',
        personality: '¿Cómo te describen las personas cercanas?',
        date_style: 'En una cita ideal, tú eres:',
        attraction: '¿Qué es lo que más te atrae en una persona? (selecciona varios)',
        dealbreakers: '¿Qué no toleras en una conexión?',
        quality: '¿Qué tan importante es para ti la calidad vs cantidad?',
        invest: '¿Estás dispuesto/a a invertir en experiencias premium?',
        ideal: '¿Qué tipo de persona te gustaría conocer?',
        incompatible: '¿Qué energía NO es para ti?',
        why_join: '¿Por qué te gustaría formar parte de Soul Mate Dates?'
      },
      lifestyle: { active: 'Activo / Fitness', social: 'Social / Eventos', professional: 'Profesional / Enfocado', balanced: 'Balanceado' },
      freetime: { experiences: 'Experiencias', growth: 'Crecimiento personal', socializing: 'Socializar', tranquility: 'Tranquilidad' },
      intention: {
        meet: 'Conocer personas nuevas', meet_desc: 'Expandir mi círculo social',
        emotional: 'Conexión emocional', emotional_desc: 'Algo más profundo que casual',
        serious: 'Relación seria', serious_desc: 'Un compromiso a largo plazo',
        open: 'No estoy seguro/a pero estoy abierto/a', open_desc: 'Ver qué surge'
      },
      datestyle: { reserved: 'Más reservado/a', balanced: 'Equilibrado/a', expressive: 'Más expresivo/a' },
      attraction: { intelligence: 'Inteligencia', energy: 'Energía', lifestyle: 'Estilo de vida', humor: 'Sentido del humor', ambition: 'Ambición', kindness: 'Bondad' },
      quality: {
        very: 'Muy importante', very_desc: 'Prefiero pocas conexiones pero profundas',
        important: 'Importante', important_desc: 'Busco un balance',
        neutral: 'Me da igual', neutral_desc: 'Estoy abierto/a a todo'
      },
      invest: { yes: 'Sí', depends: 'Depende', no: 'No' },
      nav: { prev: 'Anterior', next: 'Siguiente', submit: 'Enviar Aplicación' },
      summary: { title: 'Resumen de tu aplicación', name: 'Nombre', city: 'Ciudad', intention: 'Intención', lifestyle: 'Estilo de vida' }
    },
    // Navbar
    nav_login: 'Iniciar Sesión',
    nav_apply: 'Solicitar Acceso',
    nav_dashboard: 'Dashboard',
    nav_admin: 'Admin',
    nav_logout: 'Salir',
    
    // Hero
    hero_tagline: 'Acceso Exclusivo',
    hero_title_1: 'No estás roto…',
    hero_title_2: 'estás a punto de conectar.',
    hero_subtitle: 'Una experiencia exclusiva de conexiones curadas para quienes buscan relaciones profundas e intencionales.',
    hero_cta: 'Solicitar Acceso',
    hero_cta_dashboard: 'Ir al Dashboard',
    
    // Problem section
    problem_label: 'El Problema',
    problem_title_1: 'Las apps de citas convencionales',
    problem_title_2: 'no fueron diseñadas para ti.',
    problem_text: 'Swipes infinitos. Conversaciones vacías. Matches que no llegan a ningún lado.',
    problem_solution: 'Las conexiones reales no suceden por casualidad — suceden en espacios diseñados para ello.',
    
    // How it works
    how_label: 'Cómo Funciona',
    how_title_1: 'Un proceso diseñado para la',
    how_title_2: 'conexión auténtica',
    how_step1_title: 'Solicita',
    how_step1_desc: 'Completa un cuestionario profundo diseñado para entender quién eres realmente',
    how_step2_title: 'Selección',
    how_step2_desc: 'Nuestro equipo revisa cada aplicación. No es para todos — y eso lo hace especial',
    how_step3_title: 'Experiencia',
    how_step3_desc: 'Recibe invitaciones a eventos íntimos con personas cuidadosamente seleccionadas',
    how_step4_title: 'Conecta',
    how_step4_desc: 'Vive encuentros reales. Si hay conexión mutua, el chat se desbloquea',
    
    // Events section
    events_label: 'Experiencias Premium',
    events_title_1: 'Eventos diseñados para',
    events_title_2: 'conexiones reales',
    events_text: 'Cenas íntimas en locaciones exclusivas. Grupos pequeños de 5 a 7 personas cuidadosamente seleccionadas por compatibilidad.',
    events_feature1: 'Grupos reducidos de alta compatibilidad',
    events_feature2: 'Balance de género controlado',
    events_feature3: 'Locaciones premium privadas',
    events_feature4: 'Sin awkwardness — solo conversación genuina',
    events_quote: 'Donde cada detalle está pensado para la conexión',
    
    // Founders
    founders_label: 'La Visión',
    founders_title: 'Fundadores',
    jorge_name: 'Jorge Salcido Toledo',
    jorge_role: 'Socio Co-Fundador | Creador de la Plataforma',
    jorge_bio1: 'Jorge Salcido Toledo es socio y co-fundador de Soul Mate Dates, formando parte clave en la creación de una experiencia diseñada para redefinir la forma en que las personas se conectan.',
    jorge_bio2: 'Con una visión estratégica y un enfoque en la calidad humana, Jorge aporta estructura, criterio y dirección al proyecto, asegurando que cada detalle esté alineado con el nivel premium que caracteriza a la marca.',
    jorge_quote: 'Las conexiones reales no suceden por casualidad, sino en espacios cuidadosamente diseñados para ello.',
    flavia_name: 'Flavia Gyehl',
    flavia_role: 'CEO, Host y Productora',
    flavia_bio1: 'Conoce a Flavia Gyehl, fundadora y mente maestra detrás de Soul Mate Dates.',
    flavia_bio2: 'Una mujer apasionada por crear conexión, Flavia ha transformado las citas convencionales en una experiencia premium, elevando el mundo del dating a otro nivel.',
    flavia_bio3: 'Como CEO, host y productora, Flavia no solo dirige, sino que cuida cada detalle para asegurarse de que cada persona pueda embarcarse en un viaje auténtico hacia la conexión real.',
    
    // CTA section
    cta_label: 'Acceso Exclusivo',
    cta_title_1: 'No es para todos…',
    cta_title_2: 'y eso es lo que lo hace especial.',
    cta_text: 'Las aplicaciones están abiertas por tiempo limitado. Únete a una comunidad selecta de personas que buscan conexiones reales.',
    
    // Footer
    footer_privacy: 'Privacidad',
    footer_terms: 'Términos',
    footer_contact: 'Contacto',
    footer_rights: 'Todos los derechos reservados.',
    
    // Login
    login_welcome: 'Bienvenido de vuelta',
    login_subtitle: 'Continúa tu viaje hacia conexiones auténticas',
    login_google: 'Continuar con Google',
    login_email: 'Email',
    login_password: 'Contraseña',
    login_submit: 'Iniciar Sesión',
    login_no_account: '¿No tienes cuenta?',
    login_register: 'Regístrate',
    login_has_account: '¿Ya tienes cuenta?',
    login_login: 'Inicia sesión',
    register_title: 'Crea tu cuenta',
    register_subtitle: 'Comienza tu camino hacia relaciones significativas',
    register_name: 'Nombre',
    register_submit: 'Crear Cuenta',
    login_quote: 'No es para todos… y eso es lo que lo hace especial.',
    
    // Dashboard
    dashboard_title: 'Dashboard',
    dashboard_hello: 'Hola',
    dashboard_approved: '¡Aprobado!',
    dashboard_approved_msg: 'Tu aplicación ha sido aceptada. Bienvenido/a a Soul Mate Dates.',
    dashboard_pending: 'En Revisión',
    dashboard_pending_msg: 'Tu aplicación está siendo evaluada por nuestro equipo.',
    dashboard_waitlisted: 'Lista de Espera',
    dashboard_waitlisted_msg: 'Estás en nuestra lista de espera. Te contactaremos pronto.',
    dashboard_rejected: 'No Aprobado',
    dashboard_rejected_msg: 'Tu aplicación no fue aceptada en este momento.',
    dashboard_no_app: 'Sin Aplicación',
    dashboard_no_app_msg: 'Completa tu aplicación para acceder a experiencias exclusivas.',
    dashboard_complete_app: 'Completar Aplicación',
    dashboard_events: 'Próximos Eventos',
    dashboard_events_count: 'eventos',
    dashboard_no_events: 'Sin eventos próximos',
    dashboard_no_events_msg: 'Estamos preparando experiencias exclusivas para ti. Te notificaremos pronto.',
    dashboard_connections: 'Mis Conexiones',
    dashboard_matches: 'matches',
    dashboard_no_matches: 'Sin conexiones aún',
    dashboard_no_matches_msg: 'Asiste a eventos y haz selecciones para crear matches mutuos.',
    dashboard_memberships: 'Membresías',
    dashboard_current_plan: 'Plan Actual',
    dashboard_select: 'Seleccionar',
    dashboard_confirm: 'Confirmar Asistencia',
    dashboard_confirmed: 'Confirmado',
    dashboard_mutual_match: 'Match mutuo',
    dashboard_start_chat: 'Iniciar Chat',
    // Dashboard nested translations
    dashboard: {
      incomplete: {
        title: 'Completa tu Perfil',
        message: 'Para ser considerado en nuestro proceso de selección, primero debes completar tu cuestionario de compatibilidad.',
        progress: 'Progreso',
        sections: 'secciones completadas',
        continue: 'Continuar Cuestionario',
        start: 'Comenzar Cuestionario',
        why_title: '¿Por qué completar el cuestionario?',
        why_1: 'Nuestro equipo revisará tu perfil personalmente',
        why_2: 'La IA analizará tu compatibilidad potencial',
        why_3: 'Accederás a eventos exclusivos si eres aprobado',
        why_4: 'Conocerás personas cuidadosamente seleccionadas'
      },
      review: {
        title: 'Tu Aplicación Está en Revisión',
        message: 'Nuestro equipo está evaluando tu perfil y afinidad con la experiencia Soul Mate Dates. Este proceso puede tomar de 24 a 72 horas.',
        timeline: 'Estado del Proceso',
        step1_title: 'Cuestionario Enviado',
        step1_desc: 'Tu perfil ha sido recibido',
        step2_title: 'Análisis de Compatibilidad',
        step2_desc: 'Nuestra IA evalúa tu afinidad',
        step3_title: 'Revisión del Equipo',
        step3_desc: 'Revisión personalizada en curso',
        step4_title: 'Decisión Final',
        step4_desc: 'Te notificaremos pronto',
        your_profile: 'Tu Perfil',
        name: 'Nombre',
        city: 'Ciudad',
        intention: 'Intención',
        lifestyle: 'Estilo de Vida'
      },
      waitlist: {
        title: 'Estás en Lista de Espera',
        message: 'Gracias por tu interés en Soul Mate Dates. Actualmente estás en nuestra lista de espera para acceso exclusivo.',
        what_means: '¿Qué significa esto?',
        exclusive: 'Mantenemos el acceso limitado para garantizar la calidad',
        notify: 'Te notificaremos por email cuando se abra un espacio',
        priority: 'Tu perfil está listo para cuando haya disponibilidad',
        questions: '¿Tienes preguntas?',
        contact: 'Contáctanos'
      },
      rejected: {
        title: 'Aplicación No Aprobada',
        message: 'Después de revisar tu perfil, hemos determinado que no es el momento adecuado para tu ingreso.',
        note: 'Las circunstancias cambian y siempre buscamos personas comprometidas con conexiones auténticas.'
      },
      approved: {
        welcome: '¡Bienvenido a Soul Mate Dates!',
        message: 'Tu aplicación ha sido aprobada. Ahora tienes acceso a experiencias exclusivas.'
      },
      tabs: {
        connections: 'Conexiones Sugeridas',
        events: 'Mis Eventos',
        matches: 'Mis Matches',
        profile: 'Mi Perfil',
        experience: 'Mi Experiencia'
      },
      connections: {
        title: 'Conexiones Seleccionadas para Ti',
        subtitle: 'Personas cuidadosamente seleccionadas por su compatibilidad contigo.',
        empty_title: 'Preparando tus Conexiones',
        empty_message: 'Estamos analizando tu perfil para encontrar personas compatibles.',
        affinity: 'Afinidad',
        affinity_high: 'Alta compatibilidad para experiencias curadas',
        affinity_medium: 'Comparten intención de conexión profunda',
        affinity_base: 'Afinidad en estilo de vida y valores',
        meet_at_event: 'Podrás conocerla en próximas experiencias',
        philosophy: 'Cada conexión ha sido seleccionada pensando en calidad, no cantidad.'
      },
      events: {
        title: 'Mis Eventos',
        subtitle: 'Experiencias exclusivas diseñadas para conexiones auténticas.',
        empty_title: 'Próximamente',
        empty_message: 'Estamos preparando experiencias exclusivas para ti. Te notificaremos cuando tengas una invitación.',
        attendees: 'asistentes',
        pending_invitations: 'Invitaciones Pendientes',
        confirmed: 'Eventos Confirmados',
        history: 'Historial',
        invitation: 'Invitación',
        accept: 'Aceptar',
        decline: 'Rechazar',
        confirmed_badge: 'Confirmado'
      },
      matches: {
        title: 'Mis Matches',
        subtitle: 'Conexiones mutuas creadas después de experiencias presenciales.',
        empty_title: 'Sin Matches Aún',
        empty_message: 'Los matches surgen después de que ambas personas se seleccionan mutuamente.',
        mutual: 'Match mutuo',
        met_at: 'Se conocieron en',
        start_chat: 'Iniciar Conversación',
        philosophy: 'Las conexiones reales suceden en persona'
      },
      profile: {
        title: 'Mi Perfil',
        subtitle: 'Tu información en Soul Mate Dates.',
        approved: 'Aprobado',
        age: 'Edad',
        city: 'Ciudad',
        occupation: 'Ocupación',
        intention: 'Intención'
      },
      experience: {
        title: 'Mi Experiencia',
        subtitle: 'Tu journey en Soul Mate Dates.',
        philosophy_title: 'Nuestra Filosofía',
        philosophy_text: 'Soul Mate Dates no es una app de citas convencional. Creemos que las conexiones profundas suceden en persona.',
        point_1: 'Grupos pequeños de alta compatibilidad',
        point_2: 'Eventos premium en locaciones exclusivas',
        point_3: 'Chat solo después de conexión mutua post-evento',
        status_title: 'Tu Estado',
        events_attended: 'Eventos asistidos',
        matches_made: 'Matches creados'
      }
    },
    
    // Membership tiers
    tier_basic: 'Básico',
    tier_premium: 'Premium',
    tier_vip: 'VIP',
    tier_popular: 'Popular',
    tier_basic_f1: 'Acceso a eventos mensuales',
    tier_basic_f2: 'Perfil verificado',
    tier_basic_f3: 'Soporte básico',
    tier_premium_f1: 'Eventos semanales',
    tier_premium_f2: 'Prioridad en matching',
    tier_premium_f3: 'Eventos VIP',
    tier_premium_f4: 'Soporte prioritario',
    tier_vip_f1: 'Acceso ilimitado',
    tier_vip_f2: 'Concierge personal',
    tier_vip_f3: 'Eventos exclusivos',
    tier_vip_f4: 'Matching personalizado',
    
    // Application
    app_login_required: 'Inicia sesión para aplicar',
    app_login_msg: 'Necesitas una cuenta para completar tu aplicación a Soul Mate Dates.',
    app_step: 'Paso',
    app_of: 'de',
    app_completed: 'completado',
    app_prev: 'Anterior',
    app_next: 'Siguiente',
    app_submit: 'Enviar Aplicación',
    
    // App blocks
    app_block1_title: 'Datos Básicos',
    app_block1_subtitle: 'Cuéntanos sobre ti',
    app_block2_title: 'Estilo de Vida',
    app_block2_subtitle: 'Tu forma de vivir',
    app_block3_title: 'Intención',
    app_block3_subtitle: '¿Qué estás buscando?',
    app_block4_title: 'Personalidad',
    app_block4_subtitle: 'Cómo eres en citas',
    app_block5_title: 'Conexión',
    app_block5_subtitle: 'Lo que te atrae',
    app_block6_title: 'Nivel Premium',
    app_block6_subtitle: 'Tu compromiso',
    app_block7_title: 'Match Inteligente',
    app_block7_subtitle: 'Tu match ideal',
    app_block8_title: 'Exclusividad',
    app_block8_subtitle: '¿Por qué Soul Mate Dates?',
    
    // Form fields
    field_name: 'Nombre completo',
    field_age: 'Edad',
    field_city: 'Ciudad',
    field_city_placeholder: '¿Dónde vives?',
    field_occupation: 'Ocupación',
    field_occupation_placeholder: '¿A qué te dedicas?',
    field_instagram: 'Instagram (opcional)',
    field_lifestyle: '¿Cómo describirías tu estilo de vida?',
    field_lifestyle_active: 'Activo / Fitness',
    field_lifestyle_social: 'Social / Eventos',
    field_lifestyle_professional: 'Profesional / Enfocado',
    field_lifestyle_balanced: 'Balanceado',
    field_freetime: '¿Qué valoras más en tu tiempo libre?',
    field_freetime_experiences: 'Experiencias',
    field_freetime_growth: 'Crecimiento personal',
    field_freetime_socializing: 'Socializar',
    field_freetime_tranquility: 'Tranquilidad',
    field_looking: '¿Qué estás buscando actualmente?',
    field_looking_meet: 'Conocer personas nuevas',
    field_looking_meet_desc: 'Expandir mi círculo social',
    field_looking_emotional: 'Conexión emocional',
    field_looking_emotional_desc: 'Algo más profundo que casual',
    field_looking_serious: 'Relación seria',
    field_looking_serious_desc: 'Un compromiso a largo plazo',
    field_looking_open: 'No estoy seguro/a pero estoy abierto/a',
    field_looking_open_desc: 'Ver qué surge',
    field_personality: '¿Cómo te describen las personas cercanas?',
    field_personality_placeholder: 'Cuéntanos cómo te ven los demás...',
    field_datestyle: 'En una cita ideal, tú eres:',
    field_datestyle_reserved: 'Más reservado/a',
    field_datestyle_balanced: 'Equilibrado/a',
    field_datestyle_expressive: 'Más expresivo/a',
    field_attraction: '¿Qué es lo que más te atrae en una persona? (selecciona varios)',
    field_attraction_intelligence: 'Inteligencia',
    field_attraction_energy: 'Energía',
    field_attraction_lifestyle: 'Estilo de vida',
    field_attraction_humor: 'Sentido del humor',
    field_attraction_ambition: 'Ambición',
    field_attraction_kindness: 'Bondad',
    field_dealbreakers: '¿Qué no toleras en una conexión?',
    field_dealbreakers_placeholder: 'Sé honesto/a sobre tus límites...',
    field_quality: '¿Qué tan importante es para ti la calidad vs cantidad?',
    field_quality_very: 'Muy importante',
    field_quality_very_desc: 'Prefiero pocas conexiones pero profundas',
    field_quality_important: 'Importante',
    field_quality_important_desc: 'Busco un balance',
    field_quality_neutral: 'Me da igual',
    field_quality_neutral_desc: 'Estoy abierto/a a todo',
    field_invest: '¿Estás dispuesto/a a invertir en experiencias premium?',
    field_invest_yes: 'Sí',
    field_invest_depends: 'Depende',
    field_invest_no: 'No',
    field_ideal: '¿Qué tipo de persona te gustaría conocer?',
    field_ideal_placeholder: 'Describe a tu persona ideal...',
    field_incompatible: '¿Qué energía NO es para ti?',
    field_incompatible_placeholder: '¿Qué tipo de personas prefieres evitar?',
    field_why: '¿Por qué te gustaría formar parte de Soul Mate Dates?',
    field_why_placeholder: 'Cuéntanos tu motivación para unirte a nuestra comunidad exclusiva...',
    
    // Application status
    status_title: 'Aplicación Enviada',
    status_msg: 'Tu aplicación está siendo revisada por nuestro equipo. Te notificaremos cuando tengamos una decisión.',
    status_next: '¿Qué sigue?',
    status_step1: 'Nuestro equipo revisará tu perfil y respuestas',
    status_step2: 'La IA analizará tu compatibilidad potencial',
    status_step3: 'Recibirás una notificación con nuestra decisión',
    status_step4: 'Si eres aprobado/a, tendrás acceso a eventos exclusivos',
    
    // Admin
    admin_title: 'Panel de Administración',
    admin_subtitle: 'Admin',
    admin_users: 'Usuarios',
    admin_pending: 'Pendientes',
    admin_approved: 'Aprobados',
    admin_events: 'Eventos',
    admin_matches: 'Matches',
    admin_overview: 'Vista General',
    admin_applications: 'Aplicaciones',
    admin_create_event: 'Crear Evento',
    admin_no_apps: 'No hay aplicaciones',
    admin_no_events: 'No hay eventos',
    admin_pending_apps: 'Aplicaciones Pendientes',
    admin_recent_events: 'Eventos Recientes',
    admin_no_pending: 'No hay aplicaciones pendientes',
    admin_approve: 'Aprobar',
    admin_waitlist: 'Lista de Espera',
    admin_reject: 'Rechazar',
    admin_access_denied: 'Acceso Denegado',
    admin_no_permission: 'No tienes permisos de administrador.',
    
    // Auth
    auth_verifying: 'Verificando tu cuenta...',
    
    // Payment
    payment_success_title: '¡Pago Exitoso!',
    payment_success_msg: 'Tu pago ha sido procesado correctamente. Ya puedes disfrutar de tus beneficios.',
    payment_go_dashboard: 'Ir al Dashboard',
    payment_cancel_title: 'Pago Cancelado',
    payment_cancel_msg: 'El proceso de pago fue cancelado. Puedes intentarlo de nuevo cuando quieras.',
    payment_back: 'Volver al Dashboard',
  },
  
  en: {
    // Application questionnaire - nested keys
    app: {
      block1: { title: 'Basic Information', subtitle: 'Tell us about yourself' },
      block2: { title: 'Lifestyle', subtitle: 'Your way of living' },
      block3: { title: 'Intention', subtitle: 'What are you looking for?' },
      block4: { title: 'Personality', subtitle: 'How you are on dates' },
      block5: { title: 'Connection', subtitle: 'What attracts you' },
      block6: { title: 'Premium Level', subtitle: 'Your commitment' },
      block7: { title: 'Smart Match', subtitle: 'Your ideal match' },
      block8: { title: 'Exclusivity', subtitle: 'Why Soul Mate Dates?' },
      step: 'Step',
      of: 'of',
      saving: 'Saving...',
      saved: 'Saved',
      select: 'Select',
      login_required: 'Sign in to apply',
      login_message: 'You need an account to complete your application to Soul Mate Dates.',
      login_button: 'Continue with Google',
      error: { submit: 'Error submitting application' },
      fields: {
        name: 'Full name',
        age: 'Age',
        gender: 'Gender',
        city: 'City',
        occupation: 'Occupation',
        instagram: 'Instagram (optional)',
        interested_in: 'Interested in?'
      },
      placeholders: {
        name: 'Your name',
        age: 'Your age',
        city: 'Where do you live?',
        occupation: 'What do you do?',
        personality: 'Tell us how others see you...',
        dealbreakers: 'Be honest about your limits...',
        ideal: 'Describe your ideal person...',
        incompatible: 'What type of people do you prefer to avoid?',
        why_join: 'Tell us your motivation to join our exclusive community...'
      },
      gender: { male: 'Male', female: 'Female', other: 'Other' },
      interested: { men: 'Men', women: 'Women', all: 'Both' },
      q: {
        lifestyle: 'How would you describe your lifestyle?',
        freetime: 'What do you value most in your free time?',
        looking_for: 'What are you currently looking for?',
        personality: 'How do close people describe you?',
        date_style: 'On an ideal date, you are:',
        attraction: 'What attracts you most in a person? (select several)',
        dealbreakers: 'What do you not tolerate in a connection?',
        quality: 'How important is quality vs quantity to you?',
        invest: 'Are you willing to invest in premium experiences?',
        ideal: 'What type of person would you like to meet?',
        incompatible: 'What energy is NOT for you?',
        why_join: 'Why would you like to be part of Soul Mate Dates?'
      },
      lifestyle: { active: 'Active / Fitness', social: 'Social / Events', professional: 'Professional / Focused', balanced: 'Balanced' },
      freetime: { experiences: 'Experiences', growth: 'Personal growth', socializing: 'Socializing', tranquility: 'Tranquility' },
      intention: {
        meet: 'Meet new people', meet_desc: 'Expand my social circle',
        emotional: 'Emotional connection', emotional_desc: 'Something deeper than casual',
        serious: 'Serious relationship', serious_desc: 'A long-term commitment',
        open: "I'm not sure but I'm open", open_desc: 'See what happens'
      },
      datestyle: { reserved: 'More reserved', balanced: 'Balanced', expressive: 'More expressive' },
      attraction: { intelligence: 'Intelligence', energy: 'Energy', lifestyle: 'Lifestyle', humor: 'Sense of humor', ambition: 'Ambition', kindness: 'Kindness' },
      quality: {
        very: 'Very important', very_desc: 'I prefer few but deep connections',
        important: 'Important', important_desc: 'I seek a balance',
        neutral: 'Neutral', neutral_desc: "I'm open to everything"
      },
      invest: { yes: 'Yes', depends: 'Depends', no: 'No' },
      nav: { prev: 'Previous', next: 'Next', submit: 'Submit Application' },
      summary: { title: 'Application Summary', name: 'Name', city: 'City', intention: 'Intention', lifestyle: 'Lifestyle' }
    },
    // Navbar
    nav_login: 'Sign In',
    nav_apply: 'Request Access',
    nav_dashboard: 'Dashboard',
    nav_admin: 'Admin',
    nav_logout: 'Logout',
    
    // Hero
    hero_tagline: 'Exclusive Access',
    hero_title_1: "You're not broken…",
    hero_title_2: "you're about to connect.",
    hero_subtitle: 'An exclusive experience of curated connections for those seeking deep, intentional relationships.',
    hero_cta: 'Request Access',
    hero_cta_dashboard: 'Go to Dashboard',
    
    // Problem section
    problem_label: 'The Problem',
    problem_title_1: 'Conventional dating apps',
    problem_title_2: "weren't designed for you.",
    problem_text: 'Endless swipes. Empty conversations. Matches that go nowhere.',
    problem_solution: "Real connections don't happen by chance — they happen in spaces designed for them.",
    
    // How it works
    how_label: 'How It Works',
    how_title_1: 'A process designed for',
    how_title_2: 'authentic connection',
    how_step1_title: 'Apply',
    how_step1_desc: 'Complete a deep questionnaire designed to understand who you really are',
    how_step2_title: 'Selection',
    how_step2_desc: "Our team reviews each application. It's not for everyone — and that's what makes it special",
    how_step3_title: 'Experience',
    how_step3_desc: 'Receive invitations to intimate events with carefully selected people',
    how_step4_title: 'Connect',
    how_step4_desc: 'Live real encounters. If there is mutual connection, the chat unlocks',
    
    // Events section
    events_label: 'Premium Experiences',
    events_title_1: 'Events designed for',
    events_title_2: 'real connections',
    events_text: 'Intimate dinners in exclusive locations. Small groups of 5 to 7 people carefully selected for compatibility.',
    events_feature1: 'Small groups with high compatibility',
    events_feature2: 'Controlled gender balance',
    events_feature3: 'Private premium locations',
    events_feature4: 'No awkwardness — just genuine conversation',
    events_quote: 'Where every detail is designed for connection',
    
    // Founders
    founders_label: 'The Vision',
    founders_title: 'Founders',
    jorge_name: 'Jorge Salcido Toledo',
    jorge_role: 'Co-Founder Partner | Platform Creator',
    jorge_bio1: 'Jorge Salcido Toledo is a partner and co-founder of Soul Mate Dates, playing a key role in creating an experience designed to redefine the way people connect.',
    jorge_bio2: 'With a strategic vision and a focus on human quality, Jorge brings structure, criteria and direction to the project, ensuring that every detail is aligned with the premium level that characterizes the brand.',
    jorge_quote: "Real connections don't happen by chance, but in carefully designed spaces for it.",
    flavia_name: 'Flavia Gyehl',
    flavia_role: 'CEO, Host & Producer',
    flavia_bio1: 'Meet Flavia Gyehl, founder and mastermind behind Soul Mate Dates.',
    flavia_bio2: 'A woman passionate about creating connection, Flavia has transformed conventional dating into a premium experience, elevating the world of dating to another level.',
    flavia_bio3: 'As CEO, host and producer, Flavia not only leads, but takes care of every detail to ensure that each person can embark on an authentic journey towards real connection.',
    
    // CTA section
    cta_label: 'Exclusive Access',
    cta_title_1: 'Not for everyone…',
    cta_title_2: "and that's what makes it special.",
    cta_text: 'Applications are open for a limited time. Join a select community of people looking for real connections.',
    
    // Footer
    footer_privacy: 'Privacy',
    footer_terms: 'Terms',
    footer_contact: 'Contact',
    footer_rights: 'All rights reserved.',
    
    // Login
    login_welcome: 'Welcome back',
    login_subtitle: 'Continue your journey towards authentic connections',
    login_google: 'Continue with Google',
    login_email: 'Email',
    login_password: 'Password',
    login_submit: 'Sign In',
    login_no_account: "Don't have an account?",
    login_register: 'Sign up',
    login_has_account: 'Already have an account?',
    login_login: 'Sign in',
    register_title: 'Create your account',
    register_subtitle: 'Start your path towards meaningful relationships',
    register_name: 'Name',
    register_submit: 'Create Account',
    login_quote: "Not for everyone… and that's what makes it special.",
    
    // Dashboard
    dashboard_title: 'Dashboard',
    dashboard_hello: 'Hello',
    dashboard_approved: 'Approved!',
    dashboard_approved_msg: 'Your application has been accepted. Welcome to Soul Mate Dates.',
    dashboard_pending: 'Under Review',
    dashboard_pending_msg: 'Your application is being evaluated by our team.',
    dashboard_waitlisted: 'Waitlisted',
    dashboard_waitlisted_msg: "You're on our waiting list. We'll contact you soon.",
    dashboard_rejected: 'Not Approved',
    dashboard_rejected_msg: 'Your application was not accepted at this time.',
    dashboard_no_app: 'No Application',
    dashboard_no_app_msg: 'Complete your application to access exclusive experiences.',
    dashboard_complete_app: 'Complete Application',
    dashboard_events: 'Upcoming Events',
    dashboard_events_count: 'events',
    dashboard_no_events: 'No upcoming events',
    dashboard_no_events_msg: "We're preparing exclusive experiences for you. We'll notify you soon.",
    dashboard_connections: 'My Connections',
    dashboard_matches: 'matches',
    dashboard_no_matches: 'No connections yet',
    dashboard_no_matches_msg: 'Attend events and make selections to create mutual matches.',
    dashboard_memberships: 'Memberships',
    dashboard_current_plan: 'Current Plan',
    dashboard_select: 'Select',
    dashboard_confirm: 'Confirm Attendance',
    dashboard_confirmed: 'Confirmed',
    dashboard_mutual_match: 'Mutual match',
    dashboard_start_chat: 'Start Chat',
    // Dashboard nested translations
    dashboard: {
      tabs: {
        connections: 'Suggested Connections',
        events: 'My Events',
        matches: 'My Matches',
        profile: 'My Profile',
        experience: 'My Experience'
      },
      events: {
        title: 'My Events',
        subtitle: 'Exclusive experiences designed for authentic connections.',
        empty_title: 'Coming Soon',
        empty_message: "We're preparing exclusive experiences for you. We'll notify you when you have an invitation.",
        attendees: 'attendees',
        pending_invitations: 'Pending Invitations',
        confirmed: 'Confirmed Events',
        history: 'History',
        invitation: 'Invitation',
        accept: 'Accept',
        decline: 'Decline',
        confirmed_badge: 'Confirmed'
      }
    },
    
    // Membership tiers
    tier_basic: 'Basic',
    tier_premium: 'Premium',
    tier_vip: 'VIP',
    tier_popular: 'Popular',
    tier_basic_f1: 'Access to monthly events',
    tier_basic_f2: 'Verified profile',
    tier_basic_f3: 'Basic support',
    tier_premium_f1: 'Weekly events',
    tier_premium_f2: 'Matching priority',
    tier_premium_f3: 'VIP events',
    tier_premium_f4: 'Priority support',
    tier_vip_f1: 'Unlimited access',
    tier_vip_f2: 'Personal concierge',
    tier_vip_f3: 'Exclusive events',
    tier_vip_f4: 'Personalized matching',
    
    // Application
    app_login_required: 'Sign in to apply',
    app_login_msg: 'You need an account to complete your application to Soul Mate Dates.',
    app_step: 'Step',
    app_of: 'of',
    app_completed: 'completed',
    app_prev: 'Previous',
    app_next: 'Next',
    app_submit: 'Submit Application',
    
    // App blocks
    app_block1_title: 'Basic Information',
    app_block1_subtitle: 'Tell us about yourself',
    app_block2_title: 'Lifestyle',
    app_block2_subtitle: 'Your way of living',
    app_block3_title: 'Intention',
    app_block3_subtitle: 'What are you looking for?',
    app_block4_title: 'Personality',
    app_block4_subtitle: 'How you are on dates',
    app_block5_title: 'Connection',
    app_block5_subtitle: 'What attracts you',
    app_block6_title: 'Premium Level',
    app_block6_subtitle: 'Your commitment',
    app_block7_title: 'Smart Match',
    app_block7_subtitle: 'Your ideal match',
    app_block8_title: 'Exclusivity',
    app_block8_subtitle: 'Why Soul Mate Dates?',
    
    // Form fields
    field_name: 'Full name',
    field_age: 'Age',
    field_city: 'City',
    field_city_placeholder: 'Where do you live?',
    field_occupation: 'Occupation',
    field_occupation_placeholder: 'What do you do?',
    field_instagram: 'Instagram (optional)',
    field_lifestyle: 'How would you describe your lifestyle?',
    field_lifestyle_active: 'Active / Fitness',
    field_lifestyle_social: 'Social / Events',
    field_lifestyle_professional: 'Professional / Focused',
    field_lifestyle_balanced: 'Balanced',
    field_freetime: 'What do you value most in your free time?',
    field_freetime_experiences: 'Experiences',
    field_freetime_growth: 'Personal growth',
    field_freetime_socializing: 'Socializing',
    field_freetime_tranquility: 'Tranquility',
    field_looking: 'What are you currently looking for?',
    field_looking_meet: 'Meet new people',
    field_looking_meet_desc: 'Expand my social circle',
    field_looking_emotional: 'Emotional connection',
    field_looking_emotional_desc: 'Something deeper than casual',
    field_looking_serious: 'Serious relationship',
    field_looking_serious_desc: 'A long-term commitment',
    field_looking_open: "I'm not sure but I'm open",
    field_looking_open_desc: 'See what happens',
    field_personality: 'How do close people describe you?',
    field_personality_placeholder: 'Tell us how others see you...',
    field_datestyle: 'On an ideal date, you are:',
    field_datestyle_reserved: 'More reserved',
    field_datestyle_balanced: 'Balanced',
    field_datestyle_expressive: 'More expressive',
    field_attraction: 'What attracts you most in a person? (select several)',
    field_attraction_intelligence: 'Intelligence',
    field_attraction_energy: 'Energy',
    field_attraction_lifestyle: 'Lifestyle',
    field_attraction_humor: 'Sense of humor',
    field_attraction_ambition: 'Ambition',
    field_attraction_kindness: 'Kindness',
    field_dealbreakers: 'What do you not tolerate in a connection?',
    field_dealbreakers_placeholder: 'Be honest about your limits...',
    field_quality: 'How important is quality vs quantity to you?',
    field_quality_very: 'Very important',
    field_quality_very_desc: 'I prefer few but deep connections',
    field_quality_important: 'Important',
    field_quality_important_desc: 'I seek a balance',
    field_quality_neutral: 'Neutral',
    field_quality_neutral_desc: "I'm open to everything",
    field_invest: 'Are you willing to invest in premium experiences?',
    field_invest_yes: 'Yes',
    field_invest_depends: 'Depends',
    field_invest_no: 'No',
    field_ideal: 'What type of person would you like to meet?',
    field_ideal_placeholder: 'Describe your ideal person...',
    field_incompatible: 'What energy is NOT for you?',
    field_incompatible_placeholder: 'What type of people do you prefer to avoid?',
    field_why: 'Why would you like to be part of Soul Mate Dates?',
    field_why_placeholder: 'Tell us your motivation to join our exclusive community...',
    
    // Application status
    status_title: 'Application Submitted',
    status_msg: "Your application is being reviewed by our team. We'll notify you when we have a decision.",
    status_next: "What's next?",
    status_step1: 'Our team will review your profile and responses',
    status_step2: 'AI will analyze your potential compatibility',
    status_step3: "You'll receive a notification with our decision",
    status_step4: "If approved, you'll have access to exclusive events",
    
    // Admin
    admin_title: 'Admin Panel',
    admin_subtitle: 'Admin',
    admin_users: 'Users',
    admin_pending: 'Pending',
    admin_approved: 'Approved',
    admin_events: 'Events',
    admin_matches: 'Matches',
    admin_overview: 'Overview',
    admin_applications: 'Applications',
    admin_create_event: 'Create Event',
    admin_no_apps: 'No applications',
    admin_no_events: 'No events',
    admin_pending_apps: 'Pending Applications',
    admin_recent_events: 'Recent Events',
    admin_no_pending: 'No pending applications',
    admin_approve: 'Approve',
    admin_waitlist: 'Waitlist',
    admin_reject: 'Reject',
    admin_access_denied: 'Access Denied',
    admin_no_permission: "You don't have administrator permissions.",
    
    // Auth
    auth_verifying: 'Verifying your account...',
    
    // Payment
    payment_success_title: 'Payment Successful!',
    payment_success_msg: 'Your payment has been processed correctly. You can now enjoy your benefits.',
    payment_go_dashboard: 'Go to Dashboard',
    payment_cancel_title: 'Payment Cancelled',
    payment_cancel_msg: 'The payment process was cancelled. You can try again whenever you want.',
    payment_back: 'Back to Dashboard',
  },
  
  pt: {
    // Application questionnaire - nested keys
    app: {
      block1: { title: 'Dados Básicos', subtitle: 'Conte-nos sobre você' },
      block2: { title: 'Estilo de Vida', subtitle: 'Sua forma de viver' },
      block3: { title: 'Intenção', subtitle: 'O que você está buscando?' },
      block4: { title: 'Personalidade', subtitle: 'Como você é em encontros' },
      block5: { title: 'Conexão', subtitle: 'O que te atrai' },
      block6: { title: 'Nível Premium', subtitle: 'Seu compromisso' },
      block7: { title: 'Match Inteligente', subtitle: 'Seu match ideal' },
      block8: { title: 'Exclusividade', subtitle: 'Por que Soul Mate Dates?' },
      step: 'Passo',
      of: 'de',
      saving: 'Salvando...',
      saved: 'Salvo',
      select: 'Selecionar',
      login_required: 'Entre para aplicar',
      login_message: 'Você precisa de uma conta para completar sua aplicação ao Soul Mate Dates.',
      login_button: 'Continuar com Google',
      error: { submit: 'Erro ao enviar aplicação' },
      fields: {
        name: 'Nome completo',
        age: 'Idade',
        gender: 'Gênero',
        city: 'Cidade',
        occupation: 'Ocupação',
        instagram: 'Instagram (opcional)',
        interested_in: 'Interessado/a em?'
      },
      placeholders: {
        name: 'Seu nome',
        age: 'Sua idade',
        city: 'Onde você mora?',
        occupation: 'O que você faz?',
        personality: 'Conte como os outros te veem...',
        dealbreakers: 'Seja honesto/a sobre seus limites...',
        ideal: 'Descreva sua pessoa ideal...',
        incompatible: 'Que tipo de pessoas você prefere evitar?',
        why_join: 'Conte sua motivação para se juntar à nossa comunidade exclusiva...'
      },
      gender: { male: 'Homem', female: 'Mulher', other: 'Outro' },
      interested: { men: 'Homens', women: 'Mulheres', all: 'Ambos' },
      q: {
        lifestyle: 'Como você descreveria seu estilo de vida?',
        freetime: 'O que você mais valoriza no tempo livre?',
        looking_for: 'O que você está buscando atualmente?',
        personality: 'Como as pessoas próximas te descrevem?',
        date_style: 'Em um encontro ideal, você é:',
        attraction: 'O que mais te atrai em uma pessoa? (selecione vários)',
        dealbreakers: 'O que você não tolera em uma conexão?',
        quality: 'Quão importante é qualidade vs quantidade para você?',
        invest: 'Você está disposto/a a investir em experiências premium?',
        ideal: 'Que tipo de pessoa você gostaria de conhecer?',
        incompatible: 'Que energia NÃO é para você?',
        why_join: 'Por que você gostaria de fazer parte do Soul Mate Dates?'
      },
      lifestyle: { active: 'Ativo / Fitness', social: 'Social / Eventos', professional: 'Profissional / Focado', balanced: 'Equilibrado' },
      freetime: { experiences: 'Experiências', growth: 'Crescimento pessoal', socializing: 'Socializar', tranquility: 'Tranquilidade' },
      intention: {
        meet: 'Conhecer pessoas novas', meet_desc: 'Expandir meu círculo social',
        emotional: 'Conexão emocional', emotional_desc: 'Algo mais profundo que casual',
        serious: 'Relacionamento sério', serious_desc: 'Um compromisso de longo prazo',
        open: 'Não tenho certeza mas estou aberto/a', open_desc: 'Ver o que acontece'
      },
      datestyle: { reserved: 'Mais reservado/a', balanced: 'Equilibrado/a', expressive: 'Mais expressivo/a' },
      attraction: { intelligence: 'Inteligência', energy: 'Energia', lifestyle: 'Estilo de vida', humor: 'Senso de humor', ambition: 'Ambição', kindness: 'Bondade' },
      quality: {
        very: 'Muito importante', very_desc: 'Prefiro poucas conexões mas profundas',
        important: 'Importante', important_desc: 'Busco um equilíbrio',
        neutral: 'Tanto faz', neutral_desc: 'Estou aberto/a a tudo'
      },
      invest: { yes: 'Sim', depends: 'Depende', no: 'Não' },
      nav: { prev: 'Anterior', next: 'Próximo', submit: 'Enviar Aplicação' },
      summary: { title: 'Resumo da aplicação', name: 'Nome', city: 'Cidade', intention: 'Intenção', lifestyle: 'Estilo de vida' }
    },
    // Navbar
    nav_login: 'Entrar',
    nav_apply: 'Solicitar Acesso',
    nav_dashboard: 'Painel',
    nav_admin: 'Admin',
    nav_logout: 'Sair',
    
    // Hero
    hero_tagline: 'Acesso Exclusivo',
    hero_title_1: 'Você não está quebrado…',
    hero_title_2: 'você está prestes a conectar.',
    hero_subtitle: 'Uma experiência exclusiva de conexões curadas para quem busca relacionamentos profundos e intencionais.',
    hero_cta: 'Solicitar Acesso',
    hero_cta_dashboard: 'Ir para o Painel',
    
    // Problem section
    problem_label: 'O Problema',
    problem_title_1: 'Os apps de namoro convencionais',
    problem_title_2: 'não foram feitos para você.',
    problem_text: 'Swipes infinitos. Conversas vazias. Matches que não levam a lugar nenhum.',
    problem_solution: 'Conexões reais não acontecem por acaso — acontecem em espaços projetados para isso.',
    
    // How it works
    how_label: 'Como Funciona',
    how_title_1: 'Um processo projetado para',
    how_title_2: 'conexão autêntica',
    how_step1_title: 'Solicite',
    how_step1_desc: 'Complete um questionário profundo projetado para entender quem você realmente é',
    how_step2_title: 'Seleção',
    how_step2_desc: 'Nossa equipe revisa cada aplicação. Não é para todos — e isso é o que torna especial',
    how_step3_title: 'Experiência',
    how_step3_desc: 'Receba convites para eventos íntimos com pessoas cuidadosamente selecionadas',
    how_step4_title: 'Conecte',
    how_step4_desc: 'Viva encontros reais. Se houver conexão mútua, o chat é liberado',
    
    // Events section
    events_label: 'Experiências Premium',
    events_title_1: 'Eventos projetados para',
    events_title_2: 'conexões reais',
    events_text: 'Jantares íntimos em locais exclusivos. Grupos pequenos de 5 a 7 pessoas cuidadosamente selecionadas por compatibilidade.',
    events_feature1: 'Grupos reduzidos de alta compatibilidade',
    events_feature2: 'Equilíbrio de gênero controlado',
    events_feature3: 'Locais premium privados',
    events_feature4: 'Sem constrangimento — apenas conversa genuína',
    events_quote: 'Onde cada detalhe é pensado para a conexão',
    
    // Founders
    founders_label: 'A Visão',
    founders_title: 'Fundadores',
    jorge_name: 'Jorge Salcido Toledo',
    jorge_role: 'Sócio Co-Fundador | Criador da Plataforma',
    jorge_bio1: 'Jorge Salcido Toledo é sócio e co-fundador do Soul Mate Dates, tendo papel fundamental na criação de uma experiência projetada para redefinir a forma como as pessoas se conectam.',
    jorge_bio2: 'Com visão estratégica e foco na qualidade humana, Jorge traz estrutura, critério e direção ao projeto, garantindo que cada detalhe esteja alinhado com o nível premium que caracteriza a marca.',
    jorge_quote: 'Conexões reais não acontecem por acaso, mas em espaços cuidadosamente projetados para isso.',
    flavia_name: 'Flavia Gyehl',
    flavia_role: 'CEO, Apresentadora e Produtora',
    flavia_bio1: 'Conheça Flavia Gyehl, fundadora e mente por trás do Soul Mate Dates.',
    flavia_bio2: 'Uma mulher apaixonada por criar conexão, Flavia transformou os encontros convencionais em uma experiência premium, elevando o mundo do dating a outro nível.',
    flavia_bio3: 'Como CEO, apresentadora e produtora, Flavia não apenas lidera, mas cuida de cada detalhe para garantir que cada pessoa possa embarcar em uma jornada autêntica rumo à conexão real.',
    
    // CTA section
    cta_label: 'Acesso Exclusivo',
    cta_title_1: 'Não é para todos…',
    cta_title_2: 'e é isso que torna especial.',
    cta_text: 'As inscrições estão abertas por tempo limitado. Junte-se a uma comunidade seleta de pessoas que buscam conexões reais.',
    
    // Footer
    footer_privacy: 'Privacidade',
    footer_terms: 'Termos',
    footer_contact: 'Contato',
    footer_rights: 'Todos os direitos reservados.',
    
    // Login
    login_welcome: 'Bem-vindo de volta',
    login_subtitle: 'Continue sua jornada rumo a conexões autênticas',
    login_google: 'Continuar com Google',
    login_email: 'Email',
    login_password: 'Senha',
    login_submit: 'Entrar',
    login_no_account: 'Não tem conta?',
    login_register: 'Cadastre-se',
    login_has_account: 'Já tem conta?',
    login_login: 'Entre',
    register_title: 'Crie sua conta',
    register_subtitle: 'Comece seu caminho para relacionamentos significativos',
    register_name: 'Nome',
    register_submit: 'Criar Conta',
    login_quote: 'Não é para todos… e é isso que torna especial.',
    
    // Dashboard
    dashboard_title: 'Painel',
    dashboard_hello: 'Olá',
    dashboard_approved: 'Aprovado!',
    dashboard_approved_msg: 'Sua aplicação foi aceita. Bem-vindo/a ao Soul Mate Dates.',
    dashboard_pending: 'Em Análise',
    dashboard_pending_msg: 'Sua aplicação está sendo avaliada por nossa equipe.',
    dashboard_waitlisted: 'Lista de Espera',
    dashboard_waitlisted_msg: 'Você está em nossa lista de espera. Entraremos em contato em breve.',
    dashboard_rejected: 'Não Aprovado',
    dashboard_rejected_msg: 'Sua aplicação não foi aceita neste momento.',
    dashboard_no_app: 'Sem Aplicação',
    dashboard_no_app_msg: 'Complete sua aplicação para acessar experiências exclusivas.',
    dashboard_complete_app: 'Completar Aplicação',
    dashboard_events: 'Próximos Eventos',
    dashboard_events_count: 'eventos',
    dashboard_no_events: 'Sem eventos próximos',
    dashboard_no_events_msg: 'Estamos preparando experiências exclusivas para você. Notificaremos em breve.',
    dashboard_connections: 'Minhas Conexões',
    dashboard_matches: 'matches',
    dashboard_no_matches: 'Sem conexões ainda',
    dashboard_no_matches_msg: 'Participe de eventos e faça seleções para criar matches mútuos.',
    dashboard_memberships: 'Assinaturas',
    dashboard_current_plan: 'Plano Atual',
    dashboard_select: 'Selecionar',
    dashboard_confirm: 'Confirmar Presença',
    dashboard_confirmed: 'Confirmado',
    dashboard_mutual_match: 'Match mútuo',
    dashboard_start_chat: 'Iniciar Chat',
    
    // Membership tiers
    tier_basic: 'Básico',
    tier_premium: 'Premium',
    tier_vip: 'VIP',
    tier_popular: 'Popular',
    tier_basic_f1: 'Acesso a eventos mensais',
    tier_basic_f2: 'Perfil verificado',
    tier_basic_f3: 'Suporte básico',
    tier_premium_f1: 'Eventos semanais',
    tier_premium_f2: 'Prioridade em matching',
    tier_premium_f3: 'Eventos VIP',
    tier_premium_f4: 'Suporte prioritário',
    tier_vip_f1: 'Acesso ilimitado',
    tier_vip_f2: 'Concierge pessoal',
    tier_vip_f3: 'Eventos exclusivos',
    tier_vip_f4: 'Matching personalizado',
    
    // Application
    app_login_required: 'Entre para aplicar',
    app_login_msg: 'Você precisa de uma conta para completar sua aplicação ao Soul Mate Dates.',
    app_step: 'Passo',
    app_of: 'de',
    app_completed: 'completo',
    app_prev: 'Anterior',
    app_next: 'Próximo',
    app_submit: 'Enviar Aplicação',
    
    // App blocks
    app_block1_title: 'Dados Básicos',
    app_block1_subtitle: 'Conte-nos sobre você',
    app_block2_title: 'Estilo de Vida',
    app_block2_subtitle: 'Sua forma de viver',
    app_block3_title: 'Intenção',
    app_block3_subtitle: 'O que você está buscando?',
    app_block4_title: 'Personalidade',
    app_block4_subtitle: 'Como você é em encontros',
    app_block5_title: 'Conexão',
    app_block5_subtitle: 'O que te atrai',
    app_block6_title: 'Nível Premium',
    app_block6_subtitle: 'Seu compromisso',
    app_block7_title: 'Match Inteligente',
    app_block7_subtitle: 'Seu match ideal',
    app_block8_title: 'Exclusividade',
    app_block8_subtitle: 'Por que Soul Mate Dates?',
    
    // Form fields
    field_name: 'Nome completo',
    field_age: 'Idade',
    field_city: 'Cidade',
    field_city_placeholder: 'Onde você mora?',
    field_occupation: 'Ocupação',
    field_occupation_placeholder: 'O que você faz?',
    field_instagram: 'Instagram (opcional)',
    field_lifestyle: 'Como você descreveria seu estilo de vida?',
    field_lifestyle_active: 'Ativo / Fitness',
    field_lifestyle_social: 'Social / Eventos',
    field_lifestyle_professional: 'Profissional / Focado',
    field_lifestyle_balanced: 'Equilibrado',
    field_freetime: 'O que você mais valoriza no tempo livre?',
    field_freetime_experiences: 'Experiências',
    field_freetime_growth: 'Crescimento pessoal',
    field_freetime_socializing: 'Socializar',
    field_freetime_tranquility: 'Tranquilidade',
    field_looking: 'O que você está buscando atualmente?',
    field_looking_meet: 'Conhecer pessoas novas',
    field_looking_meet_desc: 'Expandir meu círculo social',
    field_looking_emotional: 'Conexão emocional',
    field_looking_emotional_desc: 'Algo mais profundo que casual',
    field_looking_serious: 'Relacionamento sério',
    field_looking_serious_desc: 'Um compromisso de longo prazo',
    field_looking_open: 'Não tenho certeza mas estou aberto/a',
    field_looking_open_desc: 'Ver o que acontece',
    field_personality: 'Como as pessoas próximas te descrevem?',
    field_personality_placeholder: 'Conte como os outros te veem...',
    field_datestyle: 'Em um encontro ideal, você é:',
    field_datestyle_reserved: 'Mais reservado/a',
    field_datestyle_balanced: 'Equilibrado/a',
    field_datestyle_expressive: 'Mais expressivo/a',
    field_attraction: 'O que mais te atrai em uma pessoa? (selecione vários)',
    field_attraction_intelligence: 'Inteligência',
    field_attraction_energy: 'Energia',
    field_attraction_lifestyle: 'Estilo de vida',
    field_attraction_humor: 'Senso de humor',
    field_attraction_ambition: 'Ambição',
    field_attraction_kindness: 'Bondade',
    field_dealbreakers: 'O que você não tolera em uma conexão?',
    field_dealbreakers_placeholder: 'Seja honesto/a sobre seus limites...',
    field_quality: 'Quão importante é qualidade vs quantidade para você?',
    field_quality_very: 'Muito importante',
    field_quality_very_desc: 'Prefiro poucas conexões mas profundas',
    field_quality_important: 'Importante',
    field_quality_important_desc: 'Busco um equilíbrio',
    field_quality_neutral: 'Tanto faz',
    field_quality_neutral_desc: 'Estou aberto/a a tudo',
    field_invest: 'Você está disposto/a a investir em experiências premium?',
    field_invest_yes: 'Sim',
    field_invest_depends: 'Depende',
    field_invest_no: 'Não',
    field_ideal: 'Que tipo de pessoa você gostaria de conhecer?',
    field_ideal_placeholder: 'Descreva sua pessoa ideal...',
    field_incompatible: 'Que energia NÃO é para você?',
    field_incompatible_placeholder: 'Que tipo de pessoas você prefere evitar?',
    field_why: 'Por que você gostaria de fazer parte do Soul Mate Dates?',
    field_why_placeholder: 'Conte sua motivação para se juntar à nossa comunidade exclusiva...',
    
    // Application status
    status_title: 'Aplicação Enviada',
    status_msg: 'Sua aplicação está sendo revisada por nossa equipe. Notificaremos quando tivermos uma decisão.',
    status_next: 'O que vem a seguir?',
    status_step1: 'Nossa equipe revisará seu perfil e respostas',
    status_step2: 'A IA analisará sua compatibilidade potencial',
    status_step3: 'Você receberá uma notificação com nossa decisão',
    status_step4: 'Se aprovado/a, terá acesso a eventos exclusivos',
    
    // Admin
    admin_title: 'Painel de Administração',
    admin_subtitle: 'Admin',
    admin_users: 'Usuários',
    admin_pending: 'Pendentes',
    admin_approved: 'Aprovados',
    admin_events: 'Eventos',
    admin_matches: 'Matches',
    admin_overview: 'Visão Geral',
    admin_applications: 'Aplicações',
    admin_create_event: 'Criar Evento',
    admin_no_apps: 'Sem aplicações',
    admin_no_events: 'Sem eventos',
    admin_pending_apps: 'Aplicações Pendentes',
    admin_recent_events: 'Eventos Recentes',
    admin_no_pending: 'Sem aplicações pendentes',
    admin_approve: 'Aprovar',
    admin_waitlist: 'Lista de Espera',
    admin_reject: 'Rejeitar',
    admin_access_denied: 'Acesso Negado',
    admin_no_permission: 'Você não tem permissões de administrador.',
    
    // Auth
    auth_verifying: 'Verificando sua conta...',
    
    // Payment
    payment_success_title: 'Pagamento Realizado!',
    payment_success_msg: 'Seu pagamento foi processado corretamente. Agora você pode aproveitar seus benefícios.',
    payment_go_dashboard: 'Ir para o Painel',
    payment_cancel_title: 'Pagamento Cancelado',
    payment_cancel_msg: 'O processo de pagamento foi cancelado. Você pode tentar novamente quando quiser.',
    payment_back: 'Voltar ao Painel',
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('smd_language');
    return saved || 'es';
  });

  useEffect(() => {
    localStorage.setItem('smd_language', language);
  }, [language]);

  const t = (key) => {
    // Support nested keys like 'app.block1.title'
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        value = undefined;
        break;
      }
    }
    
    // Fallback to Spanish, then to key
    if (value === undefined) {
      value = translations['es'];
      for (const k of keys) {
        if (value && typeof value === 'object') {
          value = value[k];
        } else {
          value = undefined;
          break;
        }
      }
    }
    
    return value || key;
  };

  const value = {
    language,
    setLanguage,
    t,
    languages: [
      { code: 'es', name: 'Español', flag: '🇲🇽' },
      { code: 'en', name: 'English', flag: '🇺🇸' },
      { code: 'pt', name: 'Português', flag: '🇧🇷' }
    ]
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
